import { useCallback, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { SubmitHandler, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import BoardSectionList from "../../components/Kanban/kanbanCard/BoardSectionList";
import { Loading } from "../../components";
import { auth } from "../../firebase";
import { useTranslation } from "react-i18next";


import {
  addCampaign,
  editCampaign,
  fetchBrands,
  fetchCampaignById,
  fetchCampaigns,
  fetchCategories,
  fetchCountries,
  fetchProducts,
} from "../../services/campaign/campaign.api";
import { CampaignTypes } from "../../models/Campaign";
import CampaignModal from "../../components/Campaign/CampaignModal/CampaignModal";
import { ToastVariant, useToastContext } from "../../context";
import { useUserPartnerInformation } from "../../hooks";
import { PageLayout } from "../../layout/PageLayout";

const INITIAL_PAYLOAD: CampaignTypes = {
  partnerId: "",
  isPermanent: false,
  title: "",
  startDate: "",
  endDate: "",
  category: "",
  offers: [
    {
      title: "",
      cta: "",
      description: "",
      image: "",
      url: "",
    },
  ],
  parameters: {},
  active: "false",
  state: "draft",
};

const OfferRoom = () => {
  const { t } = useTranslation(); // ✅ Add this inside the function

  const [isAddCampaign, setIsAddCampaign] = useState<boolean>(false);

  const [user, loading] = useAuthState(auth);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<CampaignTypes>({
    defaultValues: {
      ...INITIAL_PAYLOAD,
    },
  });

  const [campaignId, setCampaignId] = useState<string>("");

  const [campaign, setCampaign] = useState<CampaignTypes>(INITIAL_PAYLOAD);

  const [editMode, setEditMode] = useState<boolean>(false);

  const [campaignStep, setCampaignStep] = useState<number>(1);

  const { addToast } = useToastContext();

  const { userPartnerInformation } = useUserPartnerInformation(user?.uid);
  const partnerId = userPartnerInformation?.data?.partnerId;

  const { isLoading, data } = useQuery(
    "campaigns",
    () => fetchCampaigns(userPartnerInformation?.data?.partnerId || ""),
    {
      onSuccess: (data: any) => { },
      onError: (error) => {
        addToast(ToastVariant.ERROR, {
          title: t("offer_room.error_title"),
          description: t("offer_room.error_description"),
        });
      },
      enabled: !!userPartnerInformation?.data?.partnerId,
    }
  );

  const addCampaignMutation = useMutation(addCampaign, {
    onError: () => {
      addToast(ToastVariant.ERROR, {
        title: t("offer_room.error_title"),
        description: t("offer_room.error_description"),
      });
    },
    onSuccess: () => {
      addToast(ToastVariant.SUCCESS, {
        title: "Campaign added successfully",
      });
      window.location.href = "/offer-room";
    }
  });

  const editCampaignMutation = useMutation(editCampaign, {
    onError: () => {
      addToast(ToastVariant.ERROR, {
        title: t("offer_room.error_title"),
        description: t("offer_room.error_description"),
      });
    },
    onSuccess: () => {
      addToast(ToastVariant.SUCCESS, {
        title: t("offer_room.campaign_success"),
      });
      window.location.href = "/offer-room"; // <-- FULL RELOAD
    },
  });

  const campaignValueHandler = (campaign: CampaignTypes) => {
    for (const key in campaign) {
      if (key === "offers") {
        setValue("offers", campaign[key]);
      } else if (key === "parameters") {
        setValue("parameters", campaign[key]);
      } else {
        //@ts-ignore
        setValue(key, campaign[key]);
      }
    }
  };

  const onSubmit: SubmitHandler<CampaignTypes> = useCallback(
    (data) => {
      if (editMode) {
        editCampaignMutation.mutate({
          data: {
            id: campaignId,
            ...campaign,
          },
        });
      } else {
        addCampaignMutation.mutate({
          data: {
            ...campaign,
            partnerId: userPartnerInformation?.data?.partnerId,
          },
        });
      }
    },
    [
      addCampaignMutation,
      campaign,
      campaignId,
      editCampaignMutation,
      editMode,
      userPartnerInformation,
    ]
  );

  const { isLoading: editCampaignFetchLoading } = useQuery({
    queryKey: ["campaignId", campaignId],
    queryFn: () => fetchCampaignById(campaignId),
    enabled: Boolean(campaignId),
    onSuccess: (data: any) => {
      if (data) {
        campaignValueHandler(data.campaign);
        setCampaign(data.campaign);
        setEditMode(true);
        setIsAddCampaign((prevState) => !prevState);
      }
    },
  });

  const { isLoading: categoriesLoading, data: categories } = useQuery(
    "categories",
    () => fetchCategories(partnerId as string),
    {
      onError: (error) => {
        console.error("Error", error);
      },
      select: (data: any) => data.categories,
      enabled: !!partnerId, // Only run the query when partnerId is truthy
    }
  );

  const { isLoading: productLoading, data: products } = useQuery(
    "products",
    () => fetchProducts(partnerId as string),
    {
      onError: (error) => {
        console.error("Error", error);
      },
      select: (data: any) => data.data,
      enabled: !!partnerId, // Only run when partnerId is truthy
    }
  );

  const { isLoading: brandLoading, data: brands } = useQuery(
    "brands",
    () => fetchBrands(partnerId as string), // Static ID for now
    {
      onError: (error) => {
        console.error("Error", error);
      },
      select: (data: any) => data.data,
      enabled: !!partnerId, // Always enabled since the ID is static
    }
  );

  const { isLoading: countryLoading, data: countries } = useQuery(
    "countries",
    () => fetchCountries(partnerId as string),
    {
      onError: (error) => {
        console.error("Error", error);
      },
      select: (data: any) => data.data,
      enabled: !!partnerId, // Only run when partnerId is truthy
    }
  );

  const campaignModalRender = useCallback(() => {
    return (
      <CampaignModal
        isAddCampaign={isAddCampaign}
        setIsAddCampaign={setIsAddCampaign}
        campaign={campaign}
        setCampaign={setCampaign}
        register={register}
        errors={errors}
        addCampaignMutation={addCampaignMutation}
        onSubmit={onSubmit}
        setError={setError}
        isValid={isValid}
        setCampaignStep={setCampaignStep}
        campaignStep={campaignStep}
        handleSubmit={handleSubmit}
        editMode={editMode}
        setEditMode={setEditMode}
        setCampaignId={setCampaignId}
        categories={categories}
        products={products}
        brands={brands as string[]}
        countries={countries}
        reset={reset}
      />
    );
  }, [
    addCampaignMutation,
    brands,
    campaign,
    campaignStep,
    categories,
    countries,
    editMode,
    errors,
    handleSubmit,
    isAddCampaign,
    isValid,
    onSubmit,
    products,
    register,
    setError,
    reset,
  ]);

  if (
    isLoading ||
    loading ||
    addCampaignMutation.isLoading ||
    editCampaignFetchLoading ||
    categoriesLoading ||
    productLoading ||
    brandLoading ||
    countryLoading
  ) {
    return <Loading />;
  }

  return (
    <>
      <PageLayout>
        <BoardSectionList
          setIsAddCampaignVisible={setIsAddCampaign}
          initialTaskList={data?.campaigns}
          setCampaignId={setCampaignId}
        />
      </PageLayout>

      {isAddCampaign && campaignModalRender()}
    </>
  );
};

export default OfferRoom;
